import { assertIsTruthy } from '@llx/common';
import { createContext, useCallback, useContext, useRef } from 'react';

import {
  useCreateOrUpdateSlideStatsMutation,
  useUpdateMeetingStatsMutation,
} from '@/@generated/codegen/API';
import { useCurrentMeetingDataContext } from '@/features/meeting/context/CurrentMeetingDataContext';
import useMeetingTracking from '@/features/tracking/useMeetingTracking';
import useMeetingTrackingAssetStats from '@/features/tracking/useMeetingTrackingAssetStats';
import { useOnCaseChange, useOnSlideChange } from '@/hooks/useOnValueChange';
import { usePrevious } from '@/utils/helpers/react.helper';
import { captureSentry } from '@/utils/helpers/sentryHelper';

type Props = {
  children: React.ReactNode;
};

type MeetingStatsCreationContextType = {
  trackAssetSwitch: ReturnType<typeof useMeetingTrackingAssetStats>['trackAssetSwitch'];
  trackMeetingLeave: () => void;
  trackCaseClusterStop: () => void;
  trackAnswer: (answerIndex: number, isConfirmed: boolean) => void;
  trackSlideChange: () => void;
  createNPSScoreResult: ReturnType<typeof useMeetingTracking>['createNPSScoreResult'];
};

export const MeetingStatsCreationContext = createContext<
  MeetingStatsCreationContextType | undefined
>(undefined);

export default function MeetingStatsCreationContextProvider({ children }: Props) {
  const {
    currentMeeting: meeting,
    currentPatientCaseId: currentCaseId,
    isUserHost,
  } = useCurrentMeetingDataContext();
  const startTime = useRef<number>(Date.now());
  const [mutationUpdateMeetingStats] = useUpdateMeetingStatsMutation();
  const [mutationCreateOrUpdateSlideStats] = useCreateOrUpdateSlideStatsMutation();

  const { trackAssetSwitch } = useMeetingTrackingAssetStats(meeting?.id, currentCaseId, isUserHost);
  const previousCaseId = usePrevious(currentCaseId);

  const {
    trackMeetingLeave,
    trackCaseClusterStop,
    trackAnswer,
    trackSlideChange,
    createNPSScoreResult,
  } = useMeetingTracking();

  const handleSlideChange = useCallback(
    (oldSlideId: string | null, newSlideId: string | null) => {
      assertIsTruthy(meeting?.id);
      if (!oldSlideId) return;

      if (!previousCaseId) {
        captureSentry({
          title: 'useMeetingStatsCreation: handleSlideChange previousCaseId is undefined',
          detail: {
            meetingId: meeting?.id,
            previousCaseId,
            oldSlideId,
            newSlideId,
          },
        });
        return;
      }

      const timeSpentOnSlide = Math.floor(Date.now() - startTime.current) / 1000;
      void mutationCreateOrUpdateSlideStats({
        variables: {
          data: {
            meetingId: meeting?.id,
            patientCaseId: previousCaseId,
            slideId: oldSlideId,
            time: {
              increment: Math.round(timeSpentOnSlide),
            },
          },
        },
      });
      startTime.current = Date.now();
    },
    [meeting?.id, previousCaseId, mutationCreateOrUpdateSlideStats]
  );

  const handleCaseChange = useCallback(
    (_oldPatientCaseId: string | null, newPatientCaseId: string | null) => {
      assertIsTruthy(meeting?.id);
      if (!newPatientCaseId) return;

      void mutationUpdateMeetingStats({
        variables: {
          data: {
            meetingId: meeting?.id,
            casePresentationHistory: {
              push: [newPatientCaseId],
            },
          },
        },
      });
    },
    [meeting?.id]
  );

  useOnSlideChange({
    callback: handleSlideChange,
    options: {
      skipIfPreview: true,
      skipIfTesting: true,
      customSkipIfTrue: () => {
        return !isUserHost;
      },
    },
  });

  useOnCaseChange({
    callback: handleCaseChange,
    options: {
      skipIfPreview: true,
      skipIfTesting: true,
      customSkipIfTrue: () => {
        return !isUserHost;
      },
    },
  });

  return (
    <MeetingStatsCreationContext.Provider
      value={{
        trackAssetSwitch,
        trackMeetingLeave,
        trackCaseClusterStop,
        trackAnswer,
        trackSlideChange,
        createNPSScoreResult,
      }}
    >
      {children}
    </MeetingStatsCreationContext.Provider>
  );
}

export const useMeetingStatsCreationContext = () => {
  const context = useContext(MeetingStatsCreationContext);
  if (context === undefined) {
    throw new Error(
      'useMeetingStatsCreationContext must be used within a MeetingStatsCreationContextProvider'
    );
  }
  return context;
};
